import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Group, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import { useAuth } from "@/lib/auth";
import type {
  PermissionsListResponse,
  Role,
} from "@/types/api";

export function RolePermissions({ role, onClose }: { role: Role; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("roles:manage");

  const { data: allPerms } = useQuery({
    queryKey: qk.permissions(),
    queryFn: async () => (await api.get<PermissionsListResponse>("/api/v1/permissions")).data,
  });

  const { data: roleDetail } = useQuery({
    queryKey: qk.role(role.id),
    queryFn: async () => (await api.get<Role>(`/api/v1/roles/${role.id}`)).data,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (roleDetail?.permissions) {
      setSelected(new Set(roleDetail.permissions.map((p) => p.code)));
    }
  }, [roleDetail]);

  const saveMut = useMutation({
    mutationFn: async (codes: string[]) =>
      (await api.patch<Role>(`/api/v1/roles/${role.id}`, { permissions: codes })).data,
    onSuccess: () => {
      notifications.show({ message: "Права обновлены", color: "green" });
      void qc.invalidateQueries({ queryKey: ["role", role.id] });
      void qc.invalidateQueries({ queryKey: ["roles"] });
      onClose();
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  // group by resource
  const grouped: Record<string, NonNullable<typeof allPerms>["items"]> = {};
  if (allPerms) {
    for (const p of allPerms.items) {
      (grouped[p.resource] ??= []).push(p);
    }
  }

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <Stack gap="lg">
      <div>
        <Title order={3}>{role.name}</Title>
        <Text c="dimmed" size="sm">{role.code} · {role.is_system ? "системная роль" : "пользовательская"}</Text>
      </div>

      {Object.entries(grouped).map(([resource, perms]) => (
        <Card key={resource} withBorder padding="md" radius="md">
          <Title order={5} mb="xs" tt="capitalize">{resource}</Title>
          <Stack gap="xs">
            {perms.map((p) => (
              <Checkbox
                key={p.code}
                label={`${p.name} (${p.code})`}
                checked={selected.has(p.code)}
                onChange={() => toggle(p.code)}
                disabled={!canManage}
              />
            ))}
          </Stack>
        </Card>
      ))}

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>Закрыть</Button>
        {canManage && (
          <Button
            onClick={() => saveMut.mutate(Array.from(selected))}
            loading={saveMut.isPending}
          >
            Сохранить ({selected.size})
          </Button>
        )}
      </Group>
    </Stack>
  );
}
